import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Valor } from '../../components/Valor';
import { useState } from 'react';
import { ApiError, api, type Account, type AccountType } from '../../lib/api';
import {
  ACCOUNT_TYPE_LABELS,
  ACCOUNT_TYPE_ORDER,
  INVOICE_STATUS_LABELS,
  dateBR,
  money,
  parseAmountInput,
  today,
} from '../../lib/format';

/**
 * Contas, carteiras e cartoes.
 *
 * O saldo mostrado vem calculado do servidor (saldo inicial + lancamentos), e
 * nao de um campo guardado — por isso ele sempre bate com a lista de
 * lancamentos, mesmo depois de uma exclusao.
 */
export function AccountsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const invoices = useQuery({ queryKey: ['invoices'], queryFn: () => api.invoices() });

  const archive = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.updateAccount(id, { archived }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
    onError: (caught) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteAccount(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
    onError: (caught) => setError(messageFor(caught)),
  });

  const totals = accounts.data?.totals;
  const cards = (accounts.data?.accounts ?? []).filter((a) => a.type === 'CREDIT_CARD');
  const others = (accounts.data?.accounts ?? []).filter((a) => a.type !== 'CREDIT_CARD');

  return (
    <div className="main">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>Contas</h1>
            <p>Onde o dinheiro esta. O saldo e sempre recalculado a partir dos lancamentos.</p>
          </div>
          <button className="btn" onClick={() => setShowForm((value) => !value)}>
            {showForm ? 'Cancelar' : 'Nova conta'}
          </button>
        </div>
      </header>

      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}

      {showForm && (
        <AccountForm
          accounts={accounts.data?.accounts ?? []}
          onDone={() => {
            setShowForm(false);
            setError(null);
            void queryClient.invalidateQueries({ queryKey: ['accounts'] });
          }}
          onError={setError}
        />
      )}

      {totals && (
        <div className="grid" style={{ marginBottom: 'var(--space-6)' }}>
          <article className="stat">
            <div className="stat-label">Disponivel</div>
            <div className="stat-value">
              <Valor cents={totals.availableCents} />
            </div>
          </article>
          <article className="stat">
            <div className="stat-label">Patrimonio liquido</div>
            <div className="stat-value">
              <Valor cents={totals.netWorthCents} />
            </div>
          </article>
          <article className="stat">
            <div className="stat-label">Fatura de cartao</div>
            <div className="stat-value">
              <Valor cents={totals.creditCardDebtCents} saida />
            </div>
          </article>
        </div>
      )}

      {accounts.isLoading && <p className="muted">Carregando...</p>}

      {others.length > 0 && (
        <section className="card">
          <h2 className="card-title">Contas e carteiras</h2>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {others.map((account) => (
                  <tr key={account.id}>
                    <td>
                      {account.name}
                      {account.institutionName ? (
                        <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                          {account.institutionName}
                        </div>
                      ) : (
                        <EscolherBanco account={account} />
                      )}
                      {account.archived && <span className="badge">arquivada</span>}
                    </td>
                    <td className="muted">{ACCOUNT_TYPE_LABELS[account.type]}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor cents={account.currentBalanceCents} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <AccountActions account={account} archive={archive} remove={remove} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {cards.length > 0 && (
        <section className="card">
          <h2 className="card-title">Cartoes</h2>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Cartao</th>
                  <th>Ciclo</th>
                  <th style={{ textAlign: 'right' }}>Fatura atual</th>
                  <th style={{ textAlign: 'right' }}>Limite disponivel</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cards.map((card) => (
                  <tr key={card.id}>
                    <td>
                      {card.name}
                      {/* O cartao e justamente onde o banco costuma faltar: a
                          Pluggy manda "Gold", que e nivel e nao instituicao. */}
                      {card.institutionName ? (
                        <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                          {card.institutionName}
                        </div>
                      ) : (
                        <EscolherBanco account={card} />
                      )}
                      {card.archived && <span className="badge">arquivado</span>}
                    </td>
                    <td className="muted">
                      Fecha dia {card.closingDay}, vence dia {card.dueDay}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor cents={card.currentBalanceCents} saida />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {card.availableCreditCents ? (
                        <Valor cents={card.availableCreditCents} />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <AccountActions account={card} archive={archive} remove={remove} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(invoices.data?.invoices.length ?? 0) > 0 && (
        <section className="card">
          <h2 className="card-title">Faturas</h2>
          <p className="card-hint">
            Pagar a fatura e uma transferencia, nao uma despesa nova: o gasto ja foi contado quando
            cada compra entrou.
          </p>
          <InvoiceTable
            invoices={invoices.data?.invoices ?? []}
            accounts={accounts.data?.accounts ?? []}
            onPaid={() => {
              void queryClient.invalidateQueries({ queryKey: ['invoices'] });
              void queryClient.invalidateQueries({ queryKey: ['accounts'] });
              void queryClient.invalidateQueries({ queryKey: ['transactions'] });
              void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            }}
            onError={setError}
          />
        </section>
      )}
    </div>
  );
}

function AccountActions({
  account,
  archive,
  remove,
}: {
  account: Account;
  archive: { mutate: (input: { id: string; archived: boolean }) => void };
  remove: { mutate: (id: string) => void };
}) {
  return (
    <div className="row" style={{ justifyContent: 'flex-end' }}>
      <button
        className="btn ghost"
        onClick={() => archive.mutate({ id: account.id, archived: !account.archived })}
      >
        {account.archived ? 'Reativar' : 'Arquivar'}
      </button>
      {account.transactionCount === 0 && (
        <button className="btn ghost" onClick={() => remove.mutate(account.id)}>
          Excluir
        </button>
      )}
    </div>
  );
}

/**
 * Escolher o banco de uma conta que nao tem.
 *
 * Nas conexoes de agregador (Meu Pluggy) a Pluggy nao diz a qual instituicao
 * cada conta pertence: o cartao do Itau chega so como "Gold". Adivinhar
 * seria inventar dado — "Gold" e nivel de cartao, nao banco —, entao o
 * sistema pergunta uma vez, aqui, e a partir dai o nome se completa sozinho
 * ("Cartao de credito Itau Gold ****1234").
 *
 * So aparece quando falta. Conta que ja tem banco mostra o banco e pronto.
 */
function EscolherBanco({ account }: { account: Account }) {
  const cliente = useQueryClient();
  const institutions = useQuery({ queryKey: ['institutions'], queryFn: api.institutions });

  const definir = useMutation({
    mutationFn: (institutionId: string) => api.updateAccount(account.id, { institutionId }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['accounts'] });
      void cliente.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  return (
    <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>
      <label>
        De qual banco e?{' '}
        <select
          defaultValue=""
          disabled={definir.isPending}
          onChange={(evento) => {
            if (evento.target.value) definir.mutate(evento.target.value);
          }}
        >
          <option value="">escolher...</option>
          {institutions.data?.institutions.map((instituicao) => (
            <option key={instituicao.id} value={instituicao.id}>
              {instituicao.shortName ?? instituicao.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// ------------------------------------------------------------------- forms

function AccountForm({
  accounts,
  onDone,
  onError,
}: {
  accounts: Account[];
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const institutions = useQuery({ queryKey: ['institutions'], queryFn: api.institutions });

  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('CHECKING');
  const [institutionId, setInstitutionId] = useState('');
  const [initialBalance, setInitialBalance] = useState('0,00');
  const [creditLimit, setCreditLimit] = useState('');
  const [closingDay, setClosingDay] = useState('25');
  const [dueDay, setDueDay] = useState('5');

  const isCard = type === 'CREDIT_CARD';

  const create = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        name,
        type,
        initialBalanceCents: parseAmountInput(initialBalance || '0').toString(),
        initialBalanceDate: today(),
      };
      if (institutionId) payload.institutionId = institutionId;
      if (isCard) {
        payload.closingDay = Number(closingDay);
        payload.dueDay = Number(dueDay);
        if (creditLimit) payload.creditLimitCents = parseAmountInput(creditLimit).toString();
        // O saldo inicial de um cartao e sempre zero: a saida vem das compras.
        payload.initialBalanceCents = '0';
      }
      return api.createAccount(payload);
    },
    onSuccess: onDone,
    onError: (caught) => onError(messageFor(caught)),
  });

  void accounts;

  return (
    <section className="card">
      <h2 className="card-title">Nova conta</h2>

      <div className="field">
        <label htmlFor="accountName">Nome</label>
        <input
          id="accountName"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Conta corrente, Nubank, Carteira..."
          autoFocus
        />
      </div>

      <div className="field">
        <label htmlFor="accountType">Tipo</label>
        <select
          id="accountType"
          value={type}
          onChange={(event) => setType(event.target.value as AccountType)}
        >
          {ACCOUNT_TYPE_ORDER.map((item) => (
            <option key={item} value={item}>
              {ACCOUNT_TYPE_LABELS[item]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="institution">Instituicao</label>
        <select
          id="institution"
          value={institutionId}
          onChange={(event) => setInstitutionId(event.target.value)}
        >
          <option value="">Sem instituicao</option>
          {institutions.data?.institutions.map((institution) => (
            <option key={institution.id} value={institution.id}>
              {institution.name}
            </option>
          ))}
        </select>
      </div>

      {isCard ? (
        <>
          <div className="field">
            <label htmlFor="creditLimit">Limite</label>
            <input
              id="creditLimit"
              inputMode="decimal"
              value={creditLimit}
              onChange={(event) => setCreditLimit(event.target.value)}
              placeholder="5.000,00"
            />
          </div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="closingDay">Dia de fechamento</label>
              <input
                id="closingDay"
                inputMode="numeric"
                value={closingDay}
                onChange={(event) => setClosingDay(event.target.value)}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="dueDay">Dia de vencimento</label>
              <input
                id="dueDay"
                inputMode="numeric"
                value={dueDay}
                onChange={(event) => setDueDay(event.target.value)}
              />
            </div>
          </div>
          <p className="field-hint" style={{ marginBottom: 'var(--space-4)' }}>
            Compra feita ate o dia do fechamento entra na fatura que esta fechando.
          </p>
        </>
      ) : (
        <div className="field">
          <label htmlFor="initialBalance">Saldo atual</label>
          <input
            id="initialBalance"
            inputMode="decimal"
            value={initialBalance}
            onChange={(event) => setInitialBalance(event.target.value)}
            placeholder="1.234,56"
          />
          <span className="field-hint">
            O saldo de hoje. Os lancamentos daqui para a frente somam sobre ele.
          </span>
        </div>
      )}

      <button
        className="btn"
        onClick={() => create.mutate()}
        disabled={name.trim().length < 2 || create.isPending}
      >
        {create.isPending ? 'Criando...' : 'Criar conta'}
      </button>
    </section>
  );
}

function InvoiceTable({
  invoices,
  accounts,
  onPaid,
  onError,
}: {
  invoices: Array<{
    id: string;
    accountName: string;
    dueDate: string;
    status: string;
    totalCents: string;
    paidCents: string;
  }>;
  accounts: Account[];
  onPaid: () => void;
  onError: (message: string) => void;
}) {
  const [payingId, setPayingId] = useState<string | null>(null);
  const payers = accounts.filter((account) => account.type !== 'CREDIT_CARD' && !account.archived);
  const [fromAccountId, setFromAccountId] = useState('');

  const pay = useMutation({
    mutationFn: (invoiceId: string) => api.payInvoice(invoiceId, { fromAccountId, date: today() }),
    onSuccess: () => {
      setPayingId(null);
      onPaid();
    },
    onError: (caught) => onError(messageFor(caught)),
  });

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>Cartao</th>
            <th>Vencimento</th>
            <th>Situacao</th>
            <th style={{ textAlign: 'right' }}>Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.id}>
              <td>{invoice.accountName}</td>
              <td className="tabular">{dateBR(invoice.dueDate)}</td>
              <td>
                <span className={invoice.status === 'PAID' ? 'badge' : 'badge accent'}>
                  {INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}
                </span>
              </td>
              <td className="money" style={{ textAlign: 'right' }}>
                {money(invoice.totalCents)}
              </td>
              <td style={{ textAlign: 'right' }}>
                {invoice.status !== 'PAID' && BigInt(invoice.totalCents) > 0n && (
                  <>
                    {payingId === invoice.id ? (
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <select
                          value={fromAccountId}
                          onChange={(event) => setFromAccountId(event.target.value)}
                          aria-label="Conta pagadora"
                        >
                          <option value="">Pagar com...</option>
                          {payers.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.name}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn"
                          disabled={!fromAccountId || pay.isPending}
                          onClick={() => pay.mutate(invoice.id)}
                        >
                          Confirmar
                        </button>
                        <button className="btn ghost" onClick={() => setPayingId(null)}>
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button className="btn secondary" onClick={() => setPayingId(invoice.id)}>
                        Pagar
                      </button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Nao foi possivel concluir a operacao.';
}
